/**
 * Reads dimensions (and duration for video) from a file before upload.
 *
 * The API has no ffprobe, so without this every asset lands with null width /
 * height / duration and platform rules for Reels, Shorts and Stories cannot be
 * checked until the provider rejects the post.
 */
export interface MediaDimensions {
  width: number | null;
  height: number | null;
  durationSeconds: number | null;
}

const EMPTY: MediaDimensions = { width: null, height: null, durationSeconds: null };

function probeImage(url: string): Promise<MediaDimensions> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight, durationSeconds: null });
    img.onerror = () => resolve(EMPTY);
    img.src = url;
  });
}

function probeVideo(url: string): Promise<MediaDimensions> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      resolve({
        width: video.videoWidth || null,
        height: video.videoHeight || null,
        durationSeconds: Number.isFinite(video.duration) ? Math.round(video.duration) : null,
      });
    video.onerror = () => resolve(EMPTY);
    video.src = url;
  });
}

export async function probeMedia(file: File): Promise<MediaDimensions> {
  if (typeof window === 'undefined') return EMPTY;
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');
  if (!isImage && !isVideo) return EMPTY;

  const url = URL.createObjectURL(file);
  try {
    // A corrupt file must not block the upload, so both probes resolve to empty
    // rather than rejecting; the timeout covers codecs the browser cannot open.
    return await Promise.race([
      isVideo ? probeVideo(url) : probeImage(url),
      new Promise<MediaDimensions>((resolve) => setTimeout(() => resolve(EMPTY), 8000)),
    ]);
  } finally {
    URL.revokeObjectURL(url);
  }
}
